import { tJosa } from '@mathflat/shared/i18n/tJosa'
import { observer } from 'mobx-react'
import { type ComponentProps, useEffect, useState } from 'react'

import type {
  AssignedStudent,
  SelectionContext,
} from '@/contentComponents/modal/AssignGroupStudentModal/AssignGroupStudentModal'
import AssignGroupStudentModal from '@/contentComponents/modal/AssignGroupStudentModal/AssignGroupStudentModal'
import type { Worksheet } from '@/dto'

import PendingScheduleGate from './_components/PendingScheduleGate'
import PendingStudentLabel from './_components/PendingStudentLabel'
import ScheduleToggleRow from './_components/ScheduleToggleRow'
import type { NewStudent } from './_service/AssignSchedule.service'
import { AssignScheduleService } from './_service/AssignSchedule.service'
import type { ScheduleForm } from './_service/ScheduleForm'
import { countStudentsForLimit, resolveSubmitState } from './_utils/resolveSubmitState'
import ScheduleListView from './ScheduleListView'

type ModalProps = ComponentProps<typeof AssignGroupStudentModal>

type Props = {
  worksheet: Worksheet
  assignedLists: AssignedStudent[][]
  /** 토글 OFF 확정 시 즉시 출제. 기존 서비스 콜백을 그대로 받는다 */
  onAssignNow: (students: NewStudent[]) => Promise<void | false>
  onUnassign: ModalProps['onUnassign']
  /** 새 예약이 등록됐을 때 */
  onScheduled?: (batchId: string) => void
  /** 기존 예약이 바뀌거나 취소됐을 때. 학습지 목록의 예약 표시를 갱신하는 데 쓴다 */
  onScheduleChanged?: () => void
}

/**
 * 스쿨플랫 학습지 개별 출제 모달. 공용 출제 모달 위에 예약 출제(등록·변경·취소)를 얹는다.
 * 읽는 순서: AssignScheduleService(조회·예약 상태·서버 반영) → ScheduleForm(시각) → resolveSubmitState(버튼) → 이 컴포넌트(조합)
 */
const ReservableAssignWorksheetModal = observer(
  ({
    worksheet,
    assignedLists,
    onAssignNow,
    onUnassign,
    onScheduled,
    onScheduleChanged,
  }: Props) => {
    // 모달 하나의 수명과 같은 서비스. 닫히면 함께 버려진다
    const [service] = useState(
      () =>
        new AssignScheduleService({
          worksheetId: worksheet.id,
          assignedLists,
          onAssignNow,
          onScheduled,
          onChanged: onScheduleChanged,
        }),
    )
    useEffect(() => {
      void service.load()
    }, [service])

    return (
      <PendingScheduleGate status={service.status} onRetry={service.load}>
        {service.form && (
          <ReservableAssignWorksheetModalBody
            worksheet={worksheet}
            service={service}
            form={service.form}
            onUnassign={onUnassign}
          />
        )}
      </PendingScheduleGate>
    )
  },
)

type BodyProps = {
  worksheet: Worksheet
  service: AssignScheduleService
  form: ScheduleForm
  onUnassign: ModalProps['onUnassign']
}

const ReservableAssignWorksheetModalBody = observer(
  ({ worksheet, service, form, onUnassign }: BodyProps) => {
    // 목록에서 이 학습지의 예약을 취소했을 수 있어, 돌아올 때 쓸 선택 상태를 함께 기억한다
    const [view, setView] = useState<{ kind: 'assign' } | { kind: 'list'; ctx: SelectionContext }>({
      kind: 'assign',
    })
    const backToAssign = async () => {
      if (view.kind === 'list') await service.syncWithServer(view.ctx)
      setView({ kind: 'assign' })
    }

    return (
      <>
        {view.kind === 'list' && <ScheduleListView onBack={backToAssign} />}
        {/* 목록 뷰 동안 출제 모달은 숨기기만 한다. 언마운트하면 고른 학생과 예약 폼이 초기화된다 */}
        <AssignGroupStudentModal
          hidden={view.kind === 'list'}
          title={`학습지 ${tJosa('출제')}하기`}
          titleDescription={worksheet.title}
          assignedLists={service.initialAssignedLists}
          submitLabel={(ctx) => resolveSubmitState(form, service.reservation, ctx).label}
          isSubmitDisabled={(ctx) => resolveSubmitState(form, service.reservation, ctx).disabled}
          renderFooterExtra={(ctx) => (
            <ScheduleToggleRow
              form={form}
              selectedCount={countStudentsForLimit(service.reservation, ctx)}
              onOpenList={() => setView({ kind: 'list', ctx })}
              onToggle={(next) => service.toggle(next, ctx)}
            />
          )}
          renderRightItemExtra={(student) =>
            service.isPendingStudent(student.id) ? <PendingStudentLabel /> : undefined
          }
          onBeforeMoveToLeft={service.removeStudents}
          onUnassign={onUnassign}
          beforeConfirm={service.beforeConfirm}
          onConfirm={service.confirm}
        />
      </>
    )
  },
)

export default ReservableAssignWorksheetModal
